import { Notify } from 'quasar'
import { appEnv, appMode, isAdmin, isKds, isPos, isRoam, version } from 'src/config'
import ClientVersion from 'src/models/client-version/ClientVersion'
import Site from 'src/models/site/Site'
import { reactive } from 'vue'

export default async ({ app }) => {
  const versionManager = reactive({
    updateSoftware,

    currentVersion: null,
    comparableCurrentVersion: null,
    versionAvailableOnFrontendServer: null,
    availableVersion: false,

    urgentUpdateHandler: null, // Called before reloading the app, can be used to notify the user and allows them to cancel
    minorUpdateHandler: null, // Called before reloading the app, can be used to notify the user and allows them to cancel
    updateHandler: null // Called when reloading the app, can be used to notify the user
  })

  const getVersionAvailableOnFrontendServer = async function () {
    // During the build process we werite this version.txt file to the public assets directory
    // Since the build process minimized the code, we need to look to a static file to see
    // what version was last built and therefore what version is available to download
    const response = await fetch('/version.txt?t=' + new Date().getTime())
    if (!response.ok) {
      const message = `An error has occured determining the version available on the frontend server: ${response.status}`
      console.log(message)
      return null
    }
    const version = await response.text()
    console.log('Version on Frontend Server: ' + version)

    return version
  }

  const availableClientVersions = new ClientVersion({}, {
    isCollection: true,
    queryName: 'clientVersions',
    variables: {
      appMode,
      type: 'available-versions',
      currentVersion: version,
      versionAvailableOnFrontendServer: versionManager.versionAvailableOnFrontendServer
    },
    fields: ['id', 'version', 'urgent']
  })

  async function checkVersionAndUpdate (urgentOnly = true) {
    console.log('Checking for new versions... ' + (urgentOnly ? 'Urgent Only' : 'Any Version'))

    const availableVersion = await getAvailableVersion()
    versionManager.availableVersion = availableVersion
    if (availableVersion === 'urgent') {
      console.log('Found urgent new version')
      if (versionManager.urgentUpdateHandler) {
        versionManager.urgentUpdateHandler(() => {
          updateSoftware(true)
        })
      } else {
        updateSoftware()
      }
    } else if (availableVersion === 'minor' || availableVersion === true) {
      console.log('Found minor new version')
      if (urgentOnly && !versionManager.minorUpdateHandler) return

      if (versionManager.minorUpdateHandler) {
        versionManager.minorUpdateHandler(() => {
          updateSoftware(true)
        })
      } else {
        updateSoftware()
      }
    } else {
      console.log('No new version found')
    }
  }

  async function updateSoftware (immidiately = false) {
    if (!immidiately && versionManager?.updateHandler) {
      versionManager?.updateHandler(() => {
        performUpdate()
      })
    } else {
      await performUpdate()
    }
  }

  async function performUpdate () {
    // In order to download the latest code, we need to unregister the service workers
    // otherwise we will load the cached versions from the service worker
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) =>
        Promise.all(registrations.map((r) => r.unregister()))
      )
      .then(() => window.location.reload(true))

    // This is basically a way to hault the application while the reload
    // happends and the timeout works as a watch dog to continue loading
    // if the reload doesn't happen
    await new Promise(resolve => setTimeout(resolve, UPDATE_WAIT_TIME_MS))
  }
  async function getAvailableVersion (frontendCheckOnly = false) {
    // We need to check both what the server says is the lastest and
    // see what is available to download from the client server
    // Only return true if both of those are true
    const frontendServerVersion = await getVersionAvailableOnFrontendServer()

    if (frontendServerVersion === version) {
      console.log('Up to date with version on frontend server')
      return false
    }
    versionManager.versionAvailableOnFrontendServer = frontendServerVersion

    // If we only care if a version exists on the frontend, regardless of backend
    if (frontendCheckOnly) {
      return true
    }

    try {
      await availableClientVersions.fetch(true)
    } catch (error) {
      // If error, assume there is a mismatch and trigger a reload
      return true
    }

    if (_.some(availableClientVersions.data, { urgent: true })) {
      return 'urgent'
    } else if (availableClientVersions.data.length) {
      return 'minor'
    } else {
      return true // There is a new version, but it's not represented on the server
    }
  }

  const currentSite = new Site({}, {
    queryName: 'currentSite',
    fields: ['id', 'inMaintenanceWindow']
  })
  async function inMaintenanceWindow () {
    try {
      await currentSite.fetch(true)
    } catch (error) {
      // If there is an error, assume in maintenance window
      return true
    }
    return currentSite.inMaintenanceWindow
  }

  // This will set up timers to check for new versions at a set interval
  // and also will check immidately when opening the app from sleep
  // you can specify if you want it to load nonurgent on app wake or not
  async function monitorVersions (pollingFrequencyMinutes = 5) {
    // Startup the cycle of checking on wake
    checkVersionsOnAppWake()

    // Startup the cycle of checking at set interval
    setInterval(async () => {
      // First check to see if there is even something we can update to
      if (!await getAvailableVersion(true)) {
        return
      }

      // If we got here, there is a new version. We should check to see if
      // we are in a maintence window to know if we should update only urgent
      // versions.
      const isInMaintenanceWindow = await inMaintenanceWindow()
      await checkVersionAndUpdate(!isInMaintenanceWindow)
    }, pollingFrequencyMinutes * 60 * 1000) // Every n Minutes
  }

  let lastHadFocus = new Date().getTime()

  function checkVersionsOnAppWake () {
    setTimeout(async () => {
      const now = new Date().getTime()
      // if it's been more than 5 minutes since the user has had the tab in focus
      if (document.hasFocus()) {
        if (now - lastHadFocus > 1000 * 60 * 5) {
          await asyncUpdateIfNewClientVersionExists()
        }
        lastHadFocus = now
      }
      checkVersionsOnAppWake()
    }, 500)
  }

  const NOTIF_WAIT_TIME_MS = 10000
  const UPDATE_WAIT_TIME_MS = 5000
  function createUpdateNotifications () {
    versionManager.urgentUpdateHandler = function (callback) {
      const timeoutId = setTimeout(callback, NOTIF_WAIT_TIME_MS)
      const hideNotif = Notify.create({
        position: 'top',
        progress: true,
        timeout: NOTIF_WAIT_TIME_MS,
        message: 'Urgent softare update. Update starting...',
        color: 'negative',
        icon: 'new_releases',
        actions: [
          { label: 'Update Now', color: 'yellow', handler: () => { hideNotif(); callback() } },
          { label: 'Cancel', color: 'grey', handler: () => { hideNotif(); clearTimeout(timeoutId) } }
        ]
      })
    }
    versionManager.minorUpdateHandler = function (callback) {
      if (localStorage.minorUpdateNotificationVersion >= versionManager.versionAvailableOnFrontendServer) return

      localStorage.minorUpdateNotificationVersion = versionManager.versionAvailableOnFrontendServer
      const hideNotif = Notify.create({
        position: 'top',
        progress: true,
        timeout: NOTIF_WAIT_TIME_MS,
        message: 'Software update available',
        icon: 'new_releases',
        actions: [
          { label: 'Update Now', color: 'positive', handler: () => { hideNotif(); callback() } },
          { label: 'Dismiss', handler: () => { hideNotif() } }
        ]
      })
    }
    versionManager.updateHandler = function (callback) {
      const timeoutId = setTimeout(callback, UPDATE_WAIT_TIME_MS)
      const hideNotif = Notify.create({
        position: 'top',
        progress: true,
        timeout: UPDATE_WAIT_TIME_MS,
        message: 'Applying update ...',
        icon: 'new_releases',
        color: 'negative',
        actions: [
          { label: 'Cancel', color: 'yellow', handler: () => { hideNotif(); clearTimeout(timeoutId) } }
        ]
      })
    }
  }

  async function asyncUpdateIfNewClientVersionExists () {
    if (await getAvailableVersion(true)) {
      await updateSoftware(true)
    }
  }

  function asComparableVersion (version) {
    if (!version) return '0000.0000.0000'

    // Increase the precision of each version segment to 4 digits
    // E.g. 2025.1.1 -> 2025.0001.0001
    // This way we can compare versions as strings
    return _.split(version, '.').map(versionSegment => _.padStart(versionSegment, 4, '0')).join('.')
  }

  if (isAdmin || isPos || isKds || isRoam) createUpdateNotifications()

  if (appEnv !== 'development') {
    // Check for new versions and set up monitoring

    // If there is any new version at startup time (right now as we are executing this code),
    // update now. If the app is reloading it's the right time to get the new code, this will
    // trigger yet another reload, but this time from the source instead of from cache
    await asyncUpdateIfNewClientVersionExists()

    // Kick off the live monitoring of versions
    monitorVersions(1)
  }

  versionManager.currentVersion = version
  versionManager.comparableCurrentVersion = asComparableVersion(version)
  versionManager.asComparableVersion = asComparableVersion
  versionManager.reloadVersionAvailableOnFrontendServer = async function () {
    versionManager.versionAvailableOnFrontendServer = await getVersionAvailableOnFrontendServer()
  }
  versionManager.reloadVersionAvailableOnFrontendServer()
  versionManager.performUpdate = performUpdate

  app.config.globalProperties.$versionManager = versionManager
  app.$versionManager = versionManager
  window.$versionManager = versionManager
}
